import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  MessagesSquare,
  FolderCog,
  SearchCheck,
  Wrench,
  TrendingUp,
  GraduationCap,
} from "lucide-react";
import { PublicPage, PageIntro, GREEN, GREEN_DEEP, GOLD, CREAM, INK } from "../../components/public/PublicChrome";
import { site } from "../../content/siteData";

export const SERVICE_ICONS = {
  consultoria: Briefcase,
  asesoria: MessagesSquare,
  gestion: FolderCog,
  auditoria: SearchCheck,
  regularizacion: Wrench,
  finanzas: TrendingUp,
  formacion: GraduationCap,
};

export function ServiceGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A3328]/10 border border-[#1A3328]/10">
      {site.servicios.items.map((s, idx) => {
        const Icon = SERVICE_ICONS[s.id] || Briefcase;
        return (
          <div key={s.id} className="bg-[#F7F3EB] p-7 hover:bg-white transition-colors" data-testid={`service-${s.id}`}>
            <div
              className="inline-flex h-11 w-11 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: idx % 2 === 0 ? GREEN : GOLD, color: CREAM }}
            >
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="font-display text-xl md:text-2xl mb-2" style={{ color: GREEN }}>{s.titulo}</h3>
            <p className="text-[14px] leading-relaxed" style={{ color: `${INK}99` }}>{s.desc}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function Servicios() {
  const sv = site.servicios;
  return (
    <PublicPage
      title="Servicios profesionales"
      description="Consultoría, asesoría, gestión, solución de problemas y auditoría contable y financiera para clientes de Chile, Argentina y Latinoamérica."
    >
      <PageIntro eyebrow="Servicios" title="Servicios contables, financieros y de auditoría">
        <p>{sv.intro}</p>
      </PageIntro>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-14 md:py-16">
        <ServiceGrid />
      </section>

      <section className="bg-white border-y" style={{ borderColor: `${GREEN}14` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-3xl md:text-4xl mb-3" style={{ color: GREEN }}>¿Cómo se cobran?</h2>
            <p className="text-[15px] leading-relaxed mb-6" style={{ color: `${INK}aa` }}>
              Los servicios profesionales no tienen un precio fijo publicado: cada monto se ajusta al cliente y queda
              acordado por escrito antes de empezar. Trabajamos con tres modalidades:
            </p>
            <div className="space-y-4">
              {sv.modalidades.map((m) => (
                <div key={m.titulo} className="border-l-2 pl-4" style={{ borderColor: GOLD }}>
                  <div className="font-display text-xl" style={{ color: GREEN }}>{m.titulo}</div>
                  <p className="text-[14px] leading-relaxed" style={{ color: `${INK}aa` }}>{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-3xl md:text-4xl mb-3" style={{ color: GREEN }}>Proceso de contratación</h2>
            <ol className="space-y-4 mt-6">
              {sv.proceso.map((paso, i) => (
                <li key={i} className="flex gap-4">
                  <span
                    className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 font-display text-lg"
                    style={{ backgroundColor: GREEN, color: GOLD }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-[15px] leading-relaxed pt-1" style={{ color: `${INK}cc` }}>{paso}</span>
                </li>
              ))}
            </ol>
            <p className="text-[13px] mt-6 leading-relaxed" style={{ color: `${INK}88` }}>
              Las condiciones de cancelación y reembolso de los servicios están en la{" "}
              <Link to="/reembolsos" className="underline" style={{ color: GREEN }}>Política de reembolsos y cancelación</Link>.
            </p>
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: GREEN_DEEP }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl text-white">Cuéntanos qué necesitas</h2>
            <p className="text-white/65 mt-2 text-[15px]">Agenda una reunión de asesoría en línea: elige el día y la hora que te acomoden.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/agendar"
              className="inline-flex items-center gap-2 text-[15px] font-semibold px-6 py-3.5 rounded-sm hover:opacity-90"
              style={{ backgroundColor: GOLD, color: GREEN_DEEP }}
            >
              Agendar asesoría <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/#contacto"
              className="inline-flex items-center gap-2 text-[15px] font-medium px-6 py-3.5 rounded-sm border border-white/30 text-white hover:bg-white/10"
            >
              Solicitar cotización
            </Link>
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
